/**
 * captionPayload.js — the caption load/save mappers (OPUS-1391).
 *
 * WHY THIS EXISTS
 *
 * The dashboard has no typed caption API. It saves whole scenes through
 * PUT /v1/common/scene/:id, which replaces the `captions` array wholesale
 * against a strict:false model — so ANY field the payload omits is deleted from
 * the document, and Mongoose's schema defaults do not put it back.
 *
 * That whitelist was hand-written in four places (scene details save, scene
 * management create + edit, scene import), plus an inverse mapper on load. A
 * field added to three of the four is a field that silently disappears
 * depending on which screen the operator saved from — which is exactly what
 * happened to `disabled`: it was written by the PATCH toggle, dropped on load
 * here, dropped on save, and so every Scene Details save quietly re-enabled
 * every disabled caption.
 *
 * These mappers are the single place a caption field is declared. Adding one
 * means editing this file, not hunting call-sites.
 *
 * On `layer`: membership only — which of the player's stacked layers a caption
 * renders into, never an order within that layer. Position in the captions array
 * carries no meaning and nothing may start depending on it.
 */

/**
 * The three layers the player renders (#back z-0, #main z-50, #front z-100).
 *
 * Carries its own display metadata ({value, label, color, desc}) so the Layer
 * column in the caption grid, the Add Caption dialog and the scene list's layer
 * chips all render from one definition and cannot drift apart.
 *
 * Colours are deliberately the NON-semantic palette tones. Coral, green and
 * amber already mean brand/destructive, create and warning in this app (see
 * style/opus-tokens.css) — a layer is not a status and must not read as one.
 */
export const LAYERS = [
  { value: "back", label: "Back", color: "var(--blue)", desc: "Behind everything else" },
  { value: "main", label: "Main", color: "var(--silver)", desc: "Default layer" },
  { value: "front", label: "Front", color: "var(--violet)", desc: "On top of everything" },
];

export const DEFAULT_LAYER = "main";

const LAYER_VALUES = LAYERS.map((l) => l.value);

/** Coerce anything to a valid layer. Never throws — a bad layer must not be the
 *  reason a scene fails to load, and the API's enum would reject it anyway. */
export const normalizeLayer = (value) => (LAYER_VALUES.includes(value) ? value : DEFAULT_LAYER);

/** The display metadata for a layer, for headers, chips and pills. */
export const layerMeta = (value) => LAYERS.find((l) => l.value === normalizeLayer(value));

/**
 * Group captions by layer for display, in fixed LAYERS order.
 *
 * Returns every layer, including empty ones, so the editor shows every available
 * target rather than only the ones in use.
 *
 * Rows carry the caption's ORIGINAL index, because the editor's handlers
 * (onCaptionChange, onFileChange, remove, disable) all address captions by their
 * position in the unsorted state array — grouping must not renumber them.
 *
 * Order WITHIN a group is just the incoming array order and means nothing.
 */
export const groupByLayer = (captions = []) => {
  const rows = (captions || []).map((caption, index) => ({ caption, index }));
  return LAYERS.map((layer) => ({
    ...layer,
    rows: rows.filter(({ caption }) => normalizeLayer(caption?.layer) === layer.value),
  }));
};

/**
 * API caption → editor state.
 *
 * `existingFile: true` marks a caption that already has an uploaded zip, which
 * locks its name field. `disabled` and `layer` are read back here so a save does
 * not wipe them (see the header).
 */
export const fromApiCaption = (raw = {}) => {
  const version = raw.caption_version || raw.version || "";
  return {
    id: raw.id,
    caption_name: raw.caption_name,
    description: raw.description || "",
    version,
    existingFile: true,
    fileName: raw.fileName || `${raw.caption_name || raw.name || raw.id}.zip`,
    all_versions: raw.all_versions || (version ? [version] : []),
    all_captions: raw.all_captions || [],
    disabled: !!raw.disabled,
    layer: normalizeLayer(raw.layer),
    // Which data-model profile this caption was authored/bound against (see
    // CaptionDetailPage.jsx's dataModelProfileId) — omitted from the whitelist
    // until now, which is why it never survived a save/reload round trip.
    profile_id: raw.profile_id || "",
  };
};

/**
 * Editor state → API caption.
 *
 * `caption_version` is sent alongside `version` because older readers expect it.
 * The current version is folded into all_versions so the history never loses the
 * version actually in use.
 */
export const toCaptionPayload = (caption = {}) => {
  const version = caption.version || "";
  const allVersions = caption.all_versions ? [...caption.all_versions] : version ? [version] : [];
  if (version && !allVersions.includes(version)) allVersions.push(version);

  return {
    id: caption.id,
    caption_name: caption.caption_name,
    description: caption.description || "",
    version,
    caption_version: version,
    all_versions: allVersions,
    disabled: !!caption.disabled,
    layer: normalizeLayer(caption.layer),
    profile_id: caption.profile_id || "",
  };
};

/**
 * Source caption → cross-environment import payload.
 *
 * Differs from toCaptionPayload in keeping `path` (the import re-homes it) and
 * omitting `caption_version` (the importer writes storage fields, not the
 * editor's compatibility alias).
 */
export const toImportCaption = (raw = {}) => ({
  caption_name: raw.caption_name,
  description: raw.description,
  all_versions: raw.all_versions || [],
  id: raw.id,
  path: raw.path,
  version: raw.version,
  disabled: !!raw.disabled,
  layer: normalizeLayer(raw.layer),
});
